import React, { useState, useRef, useEffect } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
// import "@react-pdf-viewer/core/lib/styles/index.css";
// import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const PrintSafeHome = () => {
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [otpGenerated, setOtpGenerated] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [otpRemaining, setOtpRemaining] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [printStatus, setPrintStatus] = useState("");
  const [history, setHistory] = useState(["No actions yet."]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfURL, setPdfURL] = useState("");
  const pdfRef = useRef();

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [], // hide sidebar
  });

  const addHistory = (message) => {
    setHistory((prev) => {
      const newEntry = `${new Date().toLocaleString()}: ${message}`;
      if (prev[0] === "No actions yet.") return [newEntry];
      return [...prev, newEntry];
    });
  };

  const generateOTP = () => Math.floor(100000 + Math.random() * 900000).toString();

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setDocumentUploaded(true);
    setPdfURL(URL.createObjectURL(file));
    setPrintStatus("");
    setOtpVerified(false);
    setOtpInput("");

    const newOTP = generateOTP();
    setOtpGenerated(newOTP);
    const expiry = new Date();
    expiry.setMinutes(expiry.getMinutes() + 10);
    setOtpExpiry(expiry);
    addHistory(`Document uploaded: ${file.name}`);
    addHistory(`OTP generated: ${newOTP} (valid 10 min)`);
  };

  const handleShare = () => {
    const message = `Think5Whys PrintSafe Authorisation Code is: ${otpGenerated}. This code is valid for 10 minutes to print the document.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
    addHistory("OTP shared via WhatsApp");
  };

  const handleVerifyOTP = () => {
    const now = new Date();
    if (!otpGenerated) {
      alert("No OTP generated yet.");
      return;
    }
    if (now > otpExpiry) {
      alert("OTP expired. Upload document again.");
      setOtpGenerated("");
      setOtpInput("");
      setOtpExpiry(null);
      setOtpRemaining("");
      return;
    }
    if (otpInput === otpGenerated) {
      setOtpVerified(true);
      addHistory("OTP verified successfully");
    } else {
      alert("Invalid OTP. Please enter the correct code.");
      addHistory("Failed OTP verification attempt");
    }
  };

  const handlePrint = () => {
    if (!otpVerified) {
      alert("Verify OTP first!");
      return;
    }
    if (pdfRef.current) {
      pdfRef.current.focus();
      pdfRef.current.contentWindow.print();
      setPrintStatus("Document printed successfully ✅");
      addHistory("Document printed successfully");
      setOtpVerified(false);
      setOtpGenerated("");
      setOtpInput("");
      setOtpExpiry(null);
      setOtpRemaining("");
    }
  };

  // OTP countdown timer
  useEffect(() => {
    if (!otpExpiry) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diff = otpExpiry - now;
      if (diff <= 0) {
        setOtpRemaining("Expired");
        clearInterval(interval);
        setOtpGenerated("");
        setOtpVerified(false);
      } else {
        const minutes = Math.floor(diff / 1000 / 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setOtpRemaining(
          `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`
        );
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [otpExpiry]);

  return (
    <div className="bg-gray-900 min-h-screen p-5 text-white font-sans">
      <header className="text-center text-lg lg:text-xl font-bold mb-6">🖨️🔒 PrintSafe</header>

      {/* Step 1: Upload Document */}
      <div className="bg-gray-800 p-5 rounded-lg shadow-md mb-5">
        <h2 className="text-xl font-semibold mb-3">Step 1: Upload Document</h2>
        <input
          type="file"
          onChange={handleUpload}
          accept="application/pdf"
          className="w-full p-2 rounded mb-3 bg-gray-700 text-white"
        />
      </div>

      {/* Step 2: OTP Input and Share */}
      {otpGenerated && (
        <div className="bg-gray-800 p-5 rounded-lg shadow-md mb-5">
          <h2 className="text-xl font-semibold mb-3">Step 2: Authorise Printer</h2>
          <p className="mb-2">
            Share this OTP with the printer:{" "}
            <span className="font-bold text-green-400">{otpGenerated}</span>
          </p>
          <p className="mb-3 text-gray-400">OTP expires in: {otpRemaining}</p>
          <button
            onClick={handleShare}
            className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded mb-3 w-24 lg:text-sm lg:w-40 text-xs"
          >
            Share on WhatsApp
          </button>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter OTP here"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              className="flex-1 p-2 rounded bg-gray-700 text-white"
            />
            <button
              onClick={handleVerifyOTP}
              className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded w-40 text-sm sm:w-24 sm:text-xs"
            >
              Verify OTP
            </button>
          </div>
        </div>
      )}

      {/* Step 3: PDF Viewer & Print */}
      {otpVerified && pdfURL && (
        <div className="relative w-full min-h-[60vh] mb-5">
          {/* Watermark overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <span className="text-white opacity-20 text-4xl md:text-6xl font-bold rotate-[-30deg] select-none">
              CONFIDENTIAL – Think5Whys
            </span>
          </div>

          {/* PDF Viewer */}
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <Viewer
              fileUrl={pdfURL}
              plugins={[defaultLayoutPluginInstance]}
              className="relative z-10"
            />
          </Worker>

          <div className="flex justify-center mt-3">
            <button
              onClick={handlePrint}
              className="bg-green-500 hover:bg-green-600 px-6 py-3 rounded w-40 text-sm sm:w-24 sm:text-xs"
            >
              I Will Print
            </button>
          </div>
          <p className="mt-2 text-center">{printStatus}</p>
        </div>
      )}

      {/* Step 4: History */}
      <div className="bg-gray-800 p-5 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-3">Step 4: History</h2>
        <ul className="list-disc list-inside">
          {history.map((item, index) => (
            <li key={index} className="mb-1">
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* CSS to hide download & print buttons */}
      <style>
        {`
          .rpv-core__toolbar-button[data-testid="download-button"],
          .rpv-core__toolbar-button[data-testid="print-button"] {
            display: none !important;
          }
        `}
      </style>
    </div>
  );
};

export default PrintSafeHome;
